import React from 'react';
import { Link } from 'react-router-dom';
import { Presentation, Heart, ArrowRight, CheckCircle, Download, Award, Users, Clock } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: Presentation,
      title: 'Mokymai įmonėms',
      description: 'Profesionalūs, praktiniai mokymai jūsų komandai',
      features: [
        'Emocinio intelekto mokymai',
        'Emocinės sveikatos komandoje mokymai',
        'Streso valdymo mokymai',
        'Konfliktų valdymo mokymai',
        'Perdegimo prevencijos mokymai',
        'Viešojo kalbėjimo mokymai',
        'Train the Trainer programa'
      ],
      link: '/mokymai',
      color: 'bg-[#4CAF50]',
      lightColor: 'bg-[#E8F5E9]',
      iconColor: 'text-[#4CAF50]'
    },
    {
      icon: Heart,
      title: 'Koučingo sesijos',
      description: 'Tai ugdomosios konsultacijos, skirtos padėti jums augti ir pasiekti užsibrėžtus tikslus. Sesijos konkrečios ir orientuotos į sprendimus.',
      features: [
        'Individualios 1 valandos sesijos nuotoliu',
        'Motyvacijos kėlimas',
        'Asmeninis augimas',
        'Pasitikėjimo savimi stiprinimas',
        'Bendravimo įgūdžių stiprinimas'
      ],
      link: '/sesijos',
      color: 'bg-[#FF9F43]',
      lightColor: 'bg-[#FFF3E0]',
      iconColor: 'text-[#FF9F43]'
    }
  ];

  const stats = [
    { number: '10+', label: 'Metų patirtis', color: 'text-[#4CAF50]', bg: 'bg-[#E8F5E9]' },
    { number: '7', label: 'Mokymų temos', color: 'text-[#FF9F43]', bg: 'bg-[#FFF3E0]' },
    { number: '3', label: 'Kalbos (LT, EN, RU)', color: 'text-[#5B9BD5]', bg: 'bg-[#E3F2FD]' }
  ];

  return (
    <div data-testid="services-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-[#E8F5E9] text-[#4CAF50] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award className="w-4 h-4" />
              <span>Profesionalios paslaugos</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold leading-tight text-[#2D3748] mb-6">
              Mano <span className="text-[#4CAF50]">Paslaugos</span>
            </h1>
            <p className="text-lg text-[#718096] leading-relaxed max-w-3xl mx-auto">
              Siūlau profesionalias paslaugas, kurios padės sustiprinti su emociniu intelektu susijusius įgūdžius (emocijų valdymas, konfliktų sprendimas, bendravimas su sudėtingomis asmenybėmis, sveikos emocinės atmosferos komandoje kūrimas ir pan.)
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  data-testid={`service-${index}`}
                  className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  {/* Header */}
                  <div className={`${service.color} p-8`}>
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    <h2 className="text-3xl font-['Poppins'] font-bold text-white mb-3">
                      {service.title}
                    </h2>
                    <p className="text-white/90 text-lg">
                      {service.description}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="p-8">
                    <h3 className="text-lg font-semibold text-[#2D3748] mb-6">Ką siūlau:</h3>
                    <ul className="space-y-4 mb-8">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className={`w-5 h-5 ${service.iconColor} flex-shrink-0 mt-0.5`} />
                          <span className="text-[#718096]">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Link
                      to={service.link}
                      data-testid={`service-link-${index}`}
                      className={`inline-flex items-center justify-center w-full ${service.color} text-white hover:opacity-90 rounded-full px-8 py-4 font-semibold transition-all`}
                    >
                      Sužinoti daugiau
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Me Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-8">
            Kodėl verta rinktis mane?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className={`${stat.bg} rounded-2xl p-6`}>
                <div className={`text-4xl font-bold ${stat.color} mb-2`}>{stat.number}</div>
                <p className="text-[#718096]">{stat.label}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100">
            <p className="text-lg text-[#718096] leading-relaxed">
              Esu ICC sertifikuota koučingo specialistė, turinti virš 10 metų patirtį vedant mokymus įmonėms (Girteka, IKEA, McDonald's ir kt.). Tikiu gilių pokalbių, ryšio ir tikrų pokyčių svarba.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#4CAF50] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-white mb-6">
            Pasirengę pradėti?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Susisiekite ir aptarkime, kaip galiu padėti jums ar jūsų komandai
          </p>
          <Link
            to="/kontaktai"
            data-testid="cta-contact-button"
            className="inline-flex items-center bg-white text-[#4CAF50] hover:bg-slate-100 rounded-full px-8 py-4 font-semibold transition-colors"
          >
            Susisiekti
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
